/**
 * 屋台ランキング配布用QRのURL生成・解析ユーティリティ
 */

import { getPointClaimQrBaseUrl } from './pointClaimQr';

/** 屋台ランキング画面のパス */
const STALL_RATING_PATH = 'stall-rating';

/** 屋台ランキングアクセストークンのクエリ名 */
export const STALL_RATING_ACCESS_TOKEN_QUERY_NAME = 'access_token';

/** 屋台ランキング手入力コードで使用する文字 */
export const STALL_RATING_MANUAL_ACCESS_CODE_ALPHABET =
  '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/** 屋台ランキング手入力コードの文字数 */
export const STALL_RATING_MANUAL_ACCESS_CODE_LENGTH = 8;

/** 発行QRトークンの形式（32バイトをhex化した64文字） */
const STALL_RATING_QR_ACCESS_TOKEN_PATTERN = /^[0-9a-f]{64}$/i;

/** QRと同時発行する手入力コードの形式 */
const STALL_RATING_MANUAL_ACCESS_CODE_PATTERN = new RegExp(
  `^[${STALL_RATING_MANUAL_ACCESS_CODE_ALPHABET}]{${STALL_RATING_MANUAL_ACCESS_CODE_LENGTH}}$`,
);

/**
 * 屋台ランキングのQRトークンまたは手入力コードを正規化する
 * @param {string | undefined | null} accessToken - 正規化対象
 * @returns {string} 手入力コードは大文字、QRトークンは元の表記を保った値
 */
export const normalizeStallRatingAccessToken = (accessToken) => {
  /** 前後空白を除去した候補 */
  const trimmedAccessToken = String(accessToken ?? '').trim();
  /** 大文字へ変換した手入力コード候補 */
  const normalizedManualCode = trimmedAccessToken.toUpperCase();

  return STALL_RATING_MANUAL_ACCESS_CODE_PATTERN.test(normalizedManualCode)
    ? normalizedManualCode
    : trimmedAccessToken;
};

/**
 * 屋台ランキングのQRトークンまたは手入力コードの形式を検証する
 * @param {string | undefined | null} accessToken - 検証対象
 * @returns {boolean} 妥当な場合true
 */
export const isValidStallRatingAccessToken = (accessToken) => {
  /** 正規化済みの候補 */
  const normalizedAccessToken = normalizeStallRatingAccessToken(accessToken);

  return (
    STALL_RATING_QR_ACCESS_TOKEN_PATTERN.test(normalizedAccessToken)
    || STALL_RATING_MANUAL_ACCESS_CODE_PATTERN.test(normalizedAccessToken)
  );
};

/**
 * 屋台ランキングQRに埋め込むURLを生成する
 * @param {string} accessToken - 発行直後の生アクセストークン
 * @param {string} [baseUrl] - 公開アプリURL
 * @returns {string} QR埋め込みURL
 */
export const createStallRatingQrPayload = (
  accessToken,
  baseUrl = getPointClaimQrBaseUrl(),
) => {
  /** 前後空白を除去したアクセストークン */
  const normalizedAccessToken = String(accessToken ?? '').trim();
  /** 末尾スラッシュを除去した公開URL */
  const normalizedBaseUrl = String(baseUrl ?? '').trim().replace(/\/+$/, '');
  /** URLクエリ */
  const searchParams = new URLSearchParams();
  searchParams.set(STALL_RATING_ACCESS_TOKEN_QUERY_NAME, normalizedAccessToken);

  return `${normalizedBaseUrl}/${STALL_RATING_PATH}?${searchParams.toString()}`;
};

/**
 * QR読み取り値が屋台ランキングURLの場合だけアクセストークンを抽出する
 * @param {string} value - QR読み取り値
 * @returns {string} 妥当な屋台ランキングURL内のアクセストークン。非該当時は空文字
 */
export const extractStallRatingAccessToken = (value) => {
  /** 前後空白を除去した読み取り値 */
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) {
    return '';
  }

  try {
    /** 絶対URLとして解析した読み取り値 */
    const parsedUrl = new URL(normalizedValue);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return '';
    }

    /** パスの空要素を除いたセグメント */
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    if (pathSegments[pathSegments.length - 1] !== STALL_RATING_PATH) {
      return '';
    }

    /** URLクエリ内のアクセストークン */
    const accessToken = String(
      parsedUrl.searchParams.get(STALL_RATING_ACCESS_TOKEN_QUERY_NAME) ?? '',
    ).trim();

    return STALL_RATING_QR_ACCESS_TOKEN_PATTERN.test(accessToken) ? accessToken : '';
  } catch {
    return '';
  }
};

/**
 * 手入力値から屋台ランキングのQRトークンまたは手入力コードを抽出する
 * @param {string} value - QRの案内URLまたは8文字の手入力コード
 * @returns {string} 妥当なアクセス値。非該当時は空文字
 */
export const extractStallRatingAccessTokenFromInput = (value) => {
  /** QRの案内URLから抽出した64文字トークン */
  const qrAccessToken = extractStallRatingAccessToken(value);

  if (qrAccessToken) {
    return qrAccessToken;
  }

  /** 大文字へ正規化した手入力コード候補 */
  const manualAccessCode = normalizeStallRatingAccessToken(value);

  return STALL_RATING_MANUAL_ACCESS_CODE_PATTERN.test(manualAccessCode)
    ? manualAccessCode
    : '';
};

/**
 * deep linkまたはアプリ内遷移のパラメータからアクセストークンを取得する
 * @param {Object | undefined} params - 画面ルートパラメータ
 * @returns {string} 妥当なアクセストークン。非該当時は空文字
 */
export const extractStallRatingAccessTokenFromParams = (params) => {
  /** snake_caseとcamelCaseを許容した候補 */
  const accessToken = params?.access_token ?? params?.accessToken ?? '';
  /** QRトークンまたは大文字へ正規化した手入力コード */
  const normalizedAccessToken = normalizeStallRatingAccessToken(accessToken);

  return isValidStallRatingAccessToken(normalizedAccessToken) ? normalizedAccessToken : '';
};
